import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { API_BASE_URL } from '../config/api';
import './AdminComponents.css';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);

  useEffect(() => {
    loadOverview();
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(loadOverview, 30000);
    setRefreshInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const getAuthHeaders = () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('auth_token='))
      ?.split('=')[1];
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const loadOverview = async () => {
    try {
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/admin/analytics/overview`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to load overview: ${response.statusText}`);
      }

      const data = await response.json();
      setOverview(data);

    } catch (err) {
      console.error('Error loading overview:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toString() || '0';
  };

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'healthy': return '#28a745';
      case 'degraded': return '#ffc107';
      case 'down': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getHealthStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return '✅';
      case 'degraded': return '⚠️';
      case 'down': return '❌';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-state">
          <h3>⚠️ Error Loading Dashboard</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={loadOverview} className="retry-button">
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div>
          <h2>📊 Admin Dashboard</h2>
          <p>System overview and key metrics</p>
        </div>
        <div className="dashboard-actions">
          <button onClick={loadOverview} className="refresh-button" disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {overview && (
        <>
          {/* Key Metrics Cards */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">👥</div>
              <div className="metric-content">
                <div className="metric-value">{formatNumber(overview.totalUsers)}</div>
                <div className="metric-label">Total Users</div>
                <div className="metric-detail">
                  {formatNumber(overview.activeUsers)} active • {formatNumber(overview.adminUsers)} admins
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">💬</div>
              <div className="metric-content">
                <div className="metric-value">{formatNumber(overview.totalConversations)}</div>
                <div className="metric-label">Conversations</div>
                <div className="metric-detail">
                  {formatNumber(overview.totalMessages)} total messages
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">{getHealthStatusIcon(overview.systemHealth?.databaseStatus)}</div>
              <div className="metric-content">
                <div className="metric-value" style={{ color: getHealthStatusColor(overview.systemHealth?.databaseStatus) }}>
                  {overview.systemHealth?.databaseStatus || 'Unknown'}
                </div>
                <div className="metric-label">System Health</div>
                <div className="metric-detail">
                  {overview.systemHealth?.apiResponseTime}ms avg • {(overview.systemHealth?.errorRate * 100).toFixed(2)}% errors
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">📈</div>
              <div className="metric-content">
                <div className="metric-value">{formatNumber(overview.totalMessages)}</div>
                <div className="metric-label">Total Messages</div>
                <div className="metric-detail">
                  Across all conversations
                </div>
              </div>
            </div>
          </div>

          {/* System Health Details */}
          <div className="dashboard-section">
            <h3>🔧 System Health</h3>
            <div className="health-grid">
              <div className="health-card">
                <div className="health-header">
                  <span className="health-icon">{getHealthStatusIcon(overview.systemHealth?.databaseStatus)}</span>
                  <span className="health-title">Database</span>
                  <span 
                    className="health-status"
                    style={{ color: getHealthStatusColor(overview.systemHealth?.databaseStatus) }}
                  >
                    {overview.systemHealth?.databaseStatus || 'Unknown'}
                  </span>
                </div>
                <div className="health-details">
                  <div className="health-metric">
                    <span>Response Time:</span>
                    <span>{overview.systemHealth?.apiResponseTime || 0}ms</span>
                  </div>
                  <div className="health-metric">
                    <span>Error Rate:</span>
                    <span>{((overview.systemHealth?.errorRate || 0) * 100).toFixed(2)}%</span>
                  </div>
                </div>
              </div>

              <div className="health-card">
                <div className="health-header">
                  <span className="health-icon">⚡</span>
                  <span className="health-title">Performance</span>
                  <span className="health-status" style={{ color: '#28a745' }}>
                    Good
                  </span>
                </div>
                <div className="health-details">
                  <div className="health-metric">
                    <span>Avg Response:</span>
                    <span>{overview.systemHealth?.apiResponseTime || 0}ms</span>
                  </div>
                  <div className="health-metric">
                    <span>Uptime:</span>
                    <span>99.9%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="dashboard-section">
            <h3>📋 Recent Admin Activity</h3>
            <div className="activity-list">
              {overview.recentActivity && overview.recentActivity.length > 0 ? (
                overview.recentActivity.map((activity, index) => (
                  <div key={activity.logId || index} className="activity-item">
                    <div className="activity-icon">
                      {activity.action.includes('view') ? '👁️' : 
                       activity.action.includes('update') ? '✏️' : 
                       activity.action.includes('create') ? '➕' : 
                       activity.action.includes('delete') ? '🗑️' : '📝'}
                    </div>
                    <div className="activity-content">
                      <div className="activity-action">
                        {activity.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      <div className="activity-details">
                        {activity.targetUserId && (
                          <span>User: {activity.targetUserId.substring(0, 8)}...</span>
                        )}
                        <span>{new Date(activity.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-activity">
                  <p>No recent admin activity</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="dashboard-section">
            <h3>⚡ Quick Actions</h3>
            <div className="quick-actions">
              <a href="/admin/users" className="quick-action-button">
                <span className="action-icon">👥</span>
                <span className="action-label">Manage Users</span>
              </a>
              <a href="/admin/analytics" className="quick-action-button">
                <span className="action-icon">📈</span>
                <span className="action-label">View Analytics</span>
              </a>
              <a href="/admin/dynamodb-stats" className="quick-action-button">
                <span className="action-icon">🗄️</span>
                <span className="action-label">DynamoDB Stats</span>
              </a>
            </div>
          </div>

          {/* Last Updated */}
          <div className="dashboard-footer">
            <p>Last updated: {new Date(overview.timestamp).toLocaleString()}</p>
            <p>Auto-refresh: Every 30 seconds</p>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;